import type { TestQuestion } from '../types';

const TTS_CHUNK_LIMIT = 3800;

function replaceLatexCommands(latex: string): string {
  let text = latex.trim();

  text = text.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '$1 over $2');
  text = text.replace(/\\sqrt\{([^{}]+)\}/g, 'square root of $1');
  text = text.replace(/\\sqrt\[([^\]]+)\]\{([^{}]+)\}/g, '$1-th root of $2');
  text = text.replace(/\\text\{([^{}]*)\}/g, '$1');
  text = text.replace(/\\mathrm\{([^{}]*)\}/g, '$1');
  text = text.replace(/\\left|\\right/g, '');
  text = text.replace(/\\times/g, ' times ');
  text = text.replace(/\\cdot/g, ' times ');
  text = text.replace(/\\div/g, ' divided by ');
  text = text.replace(/\\pm/g, ' plus or minus ');
  text = text.replace(/\\leq|\\le/g, ' less than or equal to ');
  text = text.replace(/\\geq|\\ge/g, ' greater than or equal to ');
  text = text.replace(/\\neq|\\ne/g, ' not equal to ');
  text = text.replace(/\\approx/g, ' approximately ');
  text = text.replace(/\\infty/g, ' infinity ');
  text = text.replace(/\\degree|\\circ/g, ' degrees ');
  text = text.replace(/\\%/g, ' percent ');
  text = text.replace(/\\\$/g, ' dollars ');
  text = text.replace(/\\pi/g, ' pi ');
  text = text.replace(/\\alpha/g, ' alpha ');
  text = text.replace(/\\beta/g, ' beta ');
  text = text.replace(/\\theta/g, ' theta ');
  text = text.replace(/\\lambda/g, ' lambda ');
  text = text.replace(/\\sigma/g, ' sigma ');
  text = text.replace(/\\mu/g, ' mu ');
  text = text.replace(/\\Delta/g, ' delta ');
  text = text.replace(/\^\{([^{}]+)\}/g, ' to the power of $1 ');
  text = text.replace(/\^([0-9a-zA-Z])/g, ' to the power of $1 ');
  text = text.replace(/_\{([^{}]+)\}/g, ' sub $1 ');
  text = text.replace(/_([0-9a-zA-Z])/g, ' sub $1 ');
  text = text.replace(/\\,/g, ' ');
  text = text.replace(/\\;/g, ' ');
  text = text.replace(/\\!/g, '');
  text = text.replace(/\\{2,}/g, ' ');
  text = text.replace(/[{}]/g, '');
  return text;
}

export function latexToSpeech(raw: string): string {
  return raw.replace(/\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g, (_match, block, inline) => {
    const latex = block ?? inline ?? '';
    return ` ${replaceLatexCommands(latex)} `;
  });
}

export function plainTextForSpeech(raw: string, options?: { announceUnderline?: boolean }): string {
  let text = raw ?? '';

  text = text.replace(/\|\|([^|]+)\|\|/g, (_m, inner) =>
    options?.announceUnderline ? ` underlined text ${inner} ` : ` ${inner} `
  );
  text = text.replace(/_([^_\n]+)_/g, (_m, inner) =>
    options?.announceUnderline ? ` underlined text ${inner} ` : ` ${inner} `
  );

  text = latexToSpeech(text);
  text = text.replace(/```[\s\S]*?```/g, ' ');
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/!\[[^\]]*]\([^)]*\)/g, ' ');
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  text = text.replace(/<\/?[^>]+>/g, ' ');
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/#{1,6}\s*/g, '');
  text = text.replace(/\\\$/g, ' dollars ');
  text = text.replace(/\s+/g, ' ').trim();
  return text;
}

export function buildQuestionSpeechText(question: Pick<TestQuestion, 'question' | 'choices' | 'pictureURL'>): string {
  const parts: string[] = [];
  if (question.pictureURL) {
    parts.push('This question includes a diagram that should also be viewed.');
  }
  const stem = plainTextForSpeech(question.question, { announceUnderline: true });
  if (stem) parts.push(stem);

  question.choices.forEach((choice, index) => {
    const letter = String.fromCharCode(65 + index);
    const spokenChoice = plainTextForSpeech(choice);
    if (!spokenChoice) return;
    const alreadyLabeled = /^[A-D][).:\s]/i.test(spokenChoice.trim());
    parts.push(alreadyLabeled ? spokenChoice : `Choice ${letter}. ${spokenChoice}`);
  });

  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

export function splitTextForSpeech(text: string, maxChars = TTS_CHUNK_LIMIT): string[] {
  const cleaned = text.trim();
  if (!cleaned) return [];
  if (cleaned.length <= maxChars) return [cleaned];

  const chunks: string[] = [];
  let remaining = cleaned;

  while (remaining.length > maxChars) {
    const window = remaining.slice(0, maxChars);
    const paragraphBreak = window.lastIndexOf('\n\n');
    const sentenceBreak = Math.max(
      window.lastIndexOf('. '),
      window.lastIndexOf('? '),
      window.lastIndexOf('! ')
    );
    const spaceBreak = window.lastIndexOf(' ');

    let cut = paragraphBreak > maxChars * 0.4
      ? paragraphBreak
      : sentenceBreak > maxChars * 0.4
        ? sentenceBreak + 1
        : spaceBreak > maxChars * 0.4
          ? spaceBreak
          : maxChars;

    const piece = remaining.slice(0, cut).trim();
    if (piece) chunks.push(piece);
    remaining = remaining.slice(cut).trim();
  }

  if (remaining) chunks.push(remaining);
  return chunks;
}
